"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  getAreaCodeDestinationHref,
  getBlogHrefForAreaCode,
  getBlogLinkLabelForAreaCode,
} from "@/lib/areaCodeBlogLinks";
import { searchAreaCodes } from "@/lib/areaCodeSearchCore";
import type { AreaCode } from "@/types/areaCode";
import NoResults from "@/components/NoResults";

/** Slim search payload from /api/area-codes (omits unused NANPA metadata). */
type SearchIndexRecord = Pick<
  AreaCode,
  "code" | "region" | "regionSlug" | "regionAbbr" | "country" | "cities" | "primaryCity" | "overlays"
>;

let cachedRecords: SearchIndexRecord[] | null = null;
let recordsPromise: Promise<SearchIndexRecord[]> | null = null;

function loadRecords() {
  if (cachedRecords) {
    return Promise.resolve(cachedRecords);
  }

  if (!recordsPromise) {
    recordsPromise = fetch("/api/area-codes")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load area codes");
        }
        return response.json() as Promise<SearchIndexRecord[]>;
      })
      .then((records) => {
        cachedRecords = records;
        return records;
      });
  }

  return recordsPromise;
}

export default function SearchHeroClient() {
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState<SearchIndexRecord[]>([]);
  const results = useMemo(
    () => searchAreaCodes(query, records as AreaCode[]).slice(0, 8),
    [query, records],
  );
  const showResults = query.trim().length > 0;
  const noResultsMessage = `No area code found for "${query.trim()}". Search by a NANP area code, phone number, city, state, province, territory, or covered NANP country.`;

  useEffect(() => {
    let active = true;

    const startLoad = () => {
      loadRecords()
        .then((data) => {
          if (active) {
            setRecords(data);
          }
        })
        .catch(() => {
          if (active) {
            setRecords([]);
          }
        });
    };

    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(startLoad, { timeout: 2000 });
      return () => {
        active = false;
        window.cancelIdleCallback(id);
      };
    }

    const timer = setTimeout(startLoad, 1);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  function handleSearchFocus() {
    if (records.length === 0 && !cachedRecords) {
      loadRecords()
        .then((data) => setRecords(data))
        .catch(() => setRecords([]));
    }
  }

  return (
    <div className="relative mx-auto mt-9 w-full max-w-[580px]">
      <label className="sr-only" htmlFor="global-area-code-search">
        Search area codes
      </label>
      <div className="flex items-center overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg transition-all duration-200 focus-within:border-harbor focus-within:shadow-search">
        <div className="flex shrink-0 items-center pl-4 text-slate-400">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          className="min-w-0 flex-1 bg-transparent px-3 py-3 text-base text-ink placeholder:text-slate-500 focus:outline-none"
          id="global-area-code-search"
          placeholder="Try 212, Los Angeles, or California..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={handleSearchFocus}
        />
        <div className="flex shrink-0 items-center pr-2">
          <button className="inline-flex h-10 items-center rounded-lg bg-harbor px-5 text-sm font-semibold text-white transition-all hover:bg-harbor-dark active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-harbor/30">
            Search
          </button>
        </div>
      </div>

      {showResults && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          {results.length > 0 ? (
            <ul className="divide-y divide-slate-100">
              {results.map((areaCode) => {
                  const blogHref = getBlogHrefForAreaCode(areaCode.code);
                  const label = getBlogLinkLabelForAreaCode(areaCode.code) ?? `Area code ${areaCode.code}`;
                  const href = getAreaCodeDestinationHref(areaCode.code, areaCode.regionSlug);

                  return (
                <li key={areaCode.code}>
                  <Link
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-tide focus:outline-none focus:ring-2 focus:ring-inset focus:ring-harbor/30"
                    href={href}
                    aria-label={label}
                    title={label}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-harbor text-sm font-bold text-white">
                      {areaCode.code}
                    </div>
                    <div className="min-w-0 text-left">
                      <div className="text-sm font-semibold text-slate-800">
                        {label}
                      </div>
                      <div className="truncate text-xs text-slate-600">
                        {areaCode.region}, {areaCode.country} · {areaCode.cities.slice(0, 2).join(", ")}
                        {blogHref ? " · Guide" : ""}
                      </div>
                    </div>
                    <svg
                      className="ml-auto h-4 w-4 shrink-0 text-slate-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </li>
                  );
                })}
            </ul>
          ) : (
            <div className="p-4">
              <NoResults message={noResultsMessage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
